import PropTypes from 'prop-types';
import { Fragment, useState, useEffect, useMemo } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
// hooks
import { useAuthContext } from 'src/auth/hooks';
// routes
import { useRouter } from 'src/routes/hook';
// components
import Iconify from 'src/components/iconify';
// utils
import { getReactionsFor } from 'src/utils/chat-reactions';
//
import { useGetMessage } from './hooks';

// ----------------------------------------------------------------------

// Helper function to format time with realtime updates
function useRealtimeTimestamp(createdAt) {
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const updateTime = () => {
      try {
        const formattedTime = formatDistanceToNowStrict(new Date(createdAt), {
          addSuffix: true,
        });
        setTimeAgo(formattedTime);
      } catch (error) {
        setTimeAgo('just now');
      }
    };

    // Initial update
    updateTime();

    // Update every 30 seconds for recent messages, every minute for older ones
    const messageDate = new Date(createdAt);
    const now = new Date();
    const diffMinutes = (now - messageDate) / (1000 * 60);

    // For messages less than 5 minutes old, update every 10 seconds
    // For messages less than 1 hour old, update every 30 seconds
    // For older messages, update every minute
    let interval;
    if (diffMinutes < 5) {
      interval = setInterval(updateTime, 10000); // 10 seconds
    } else if (diffMinutes < 60) {
      interval = setInterval(updateTime, 30000); // 30 seconds
    } else {
      interval = setInterval(updateTime, 60000); // 1 minute
    }

    return () => clearInterval(interval);
  }, [createdAt]);

  return timeAgo;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '🙏', '😮'];
const EMOJI_FONT_FAMILY = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

// Parse a YouTube / Vimeo / Instagram URL into an embed descriptor.
function parseChatVideoUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (m) return { provider: 'youtube', id: m[1], embed: `https://www.youtube.com/embed/${m[1]}`, original: url };
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return { provider: 'vimeo', id: m[1], embed: `https://player.vimeo.com/video/${m[1]}`, original: url };
  m = url.match(/instagram\.com\/(?:p|reel|tv)\/([^/?#]+)/);
  if (m) return { provider: 'instagram', id: m[1], embed: `https://www.instagram.com/p/${m[1]}/embed`, original: url };
  return null;
}

const URL_REGEX = /(https?:\/\/[^\s<>"]+)/g;

// Split a body into a leading reply quote and the rest. The composer prefixes
// `[#msg-<id>]\n> author: text\n\n...` so we can scroll back to the original
// message; both the marker and the `>` lines are extracted out of the rendered
// body.
const REPLY_ID_RE = /^\[#msg-([^\]]+)\]\s*$/;
function extractReplyQuote(body) {
  if (!body) return { quote: null, rest: '' };
  const lines = String(body).split('\n');
  let i = 0;
  let replyId = null;
  if (lines[i] && REPLY_ID_RE.test(lines[i])) {
    replyId = lines[i].match(REPLY_ID_RE)[1];
    i += 1;
  }
  if (!lines[i]?.startsWith('>')) {
    if (replyId == null) return { quote: null, rest: body };
    // marker present but no quote lines (rare) — skip the blank separator if any
    if (lines[i] === '') i += 1;
    return { quote: { author: '', body: '', replyId }, rest: lines.slice(i).join('\n') };
  }
  const quoteStart = i;
  while (i < lines.length && lines[i].startsWith('>')) i += 1;
  const quoteLines = lines.slice(quoteStart, i).map((l) => l.replace(/^>\s?/, ''));
  if (lines[i] === '') i += 1;
  const rest = lines.slice(i).join('\n');
  const first = quoteLines[0] || '';
  const sepIdx = first.indexOf(': ');
  let author = '';
  let quoteBody = quoteLines.join('\n');
  if (sepIdx > 0 && sepIdx < 80) {
    author = first.slice(0, sepIdx).trim();
    quoteBody = [first.slice(sepIdx + 2), ...quoteLines.slice(1)].join('\n');
  }
  return { quote: { author, body: quoteBody.trim(), replyId }, rest };
}

function getStructuredReplyQuote(replyTo) {
  if (!replyTo) return null;
  const quote = typeof replyTo === 'string' ? { body: replyTo } : replyTo;

  return {
    author: quote.authorName || quote.author || quote.senderName || '',
    body: quote.snippet || quote.body || quote.content || '',
    replyId: quote.id || quote.messageId || quote.replyId || null,
  };
}

// Resource-mention token format: @[Display Name](type-id), e.g.
// "@[Sara Doe](talent-42)". Matched first; the surrounding plain text is
// then URL-linkified. `onMention(type, id)` handles navigation.
const MENTION_REGEX = /@\[([^\]]+)\]\(([a-z]+)-(\d+)\)/g;

function linkifyUrls(text, keyPrefix) {
  if (!text) return [];
  const nodes = [];
  text.split(URL_REGEX).forEach((part, idx) => {
    URL_REGEX.lastIndex = 0;
    if (URL_REGEX.test(part)) {
      nodes.push(
        <a
          key={`${keyPrefix}-u-${idx}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    } else if (part) {
      nodes.push(<Fragment key={`${keyPrefix}-t-${idx}`}>{part}</Fragment>);
    }
  });
  return nodes;
}

// Render text with auto-linked URLs + clickable resource mentions.
// `onMention(type, id)` is invoked when a mention chip is clicked.
function linkify(text, onMention) {
  if (!text) return null;
  const str = String(text);
  const out = [];
  let cursor = 0;
  let key = 0;
  MENTION_REGEX.lastIndex = 0;
  let match = MENTION_REGEX.exec(str);
  while (match) {
    const [token, name, type, id] = match;
    if (match.index > cursor) {
      out.push(...linkifyUrls(str.slice(cursor, match.index), `s${key}`));
    }
    out.push(
      <Box
        key={`m-${key++}`}
        component="span"
        onClick={(e) => {
          e.stopPropagation();
          if (onMention) onMention(type, id);
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.75,
          mx: 0.25,
          borderRadius: 0.75,
          bgcolor: (t) => `${t.palette.primary.main}14`,
          color: 'primary.main',
          fontWeight: 600,
          fontSize: 12,
          cursor: 'pointer',
          lineHeight: 1.4,
          '&:hover': { bgcolor: (t) => `${t.palette.primary.main}24` },
        }}
      >
        @{name}
      </Box>
    );
    cursor = match.index + token.length;
    match = MENTION_REGEX.exec(str);
  }
  if (cursor < str.length) {
    out.push(...linkifyUrls(str.slice(cursor), `e${key}`));
  }
  return out;
}

export default function ChatMessageItem({ message, participants, onOpenLightbox, onReply, onReact, onDelete }) {
  const { user } = useAuthContext();
  const router = useRouter();
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [videoDialog, setVideoDialog] = useState(null);

  const handleMentionClick = (type, id) => {
    router.push(`/dashboard/proposals/${type}/${id}`);
  };

  // Split off any leading reply quote from the body.
  const { quote: replyQuote, rest: bodyRest } = useMemo(() => {
    const structuredQuote = getStructuredReplyQuote(message?.replyTo);
    if (structuredQuote) {
      return { quote: structuredQuote, rest: message?.body || '' };
    }

    return extractReplyQuote(message?.body);
  }, [message?.body, message?.replyTo]);

  const handleReplyClick = () => {
    if (!replyQuote?.replyId) return;
    const el = document.getElementById(`msg-${replyQuote.replyId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.backgroundColor = 'rgba(33, 150, 243, 0.18)';
    setTimeout(() => { el.style.backgroundColor = ''; }, 1400);
  };

  // Extract any video links from the rendered body for inline play cards.
  const videoLinks = useMemo(() => {
    const matches = String(bodyRest || '').match(URL_REGEX) || [];
    const seen = new Set();
    return matches
      .map((u) => parseChatVideoUrl(u))
      .filter((v) => v && !seen.has(v.embed) && seen.add(v.embed));
  }, [bodyRest]);

  // Reactions: prefer the live Redux value; fall back to localStorage so they
  // re-appear after a page refresh (server-side persistence isn't wired yet).
  const effectiveReactions = useMemo(() => {
    if (Array.isArray(message?.reactions) && message.reactions.length > 0) {
      return message.reactions;
    }
    return getReactionsFor(message?.id);
  }, [message?.id, message?.reactions]);

  const { me, senderDetails, hasImage } = useGetMessage({
    message,
    participants,
    currentUserId: user?.id,
  });

  const { firstName, avatarUrl } = senderDetails;

  const { body, createdAt } = message;

  // Use realtime timestamp hook
  const timeAgo = useRealtimeTimestamp(createdAt);

  // Split attachments into image vs non-image (by URL extension)
  const isImageUrl = (url) => typeof url === 'string' && /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(url);
  const attachmentUrls = Array.isArray(message.attachments) ? message.attachments : [];
  const imageAttachments = attachmentUrls.filter(isImageUrl);
  const fileAttachments = attachmentUrls.filter((u) => typeof u === 'string' && !isImageUrl(u));
  const hasInlineImages = imageAttachments.length > 0 && !hasImage; // hasImage covers the body-as-image case already
  const hasInlineFiles = fileAttachments.length > 0;

  const renderInfo = (
    <Typography
      noWrap
      variant="caption"
      sx={{
        mb: 1,
        color: 'text.disabled',
        ...(!me && {
          mr: 'auto',
        }),
      }}
    >
      {!me && `${firstName},`} &nbsp;
      {timeAgo}
    </Typography>
  );

  const hasTextBody = !hasImage && !!body;
  const hasRestText = !hasImage && !!bodyRest;
  const bubbleIsTransparent = hasImage || (!hasTextBody && hasInlineImages);

  const renderBody = (
    <Stack
      spacing={1}
      sx={{
        p: 1.5,
        minWidth: 48,
        maxWidth: 320,
        borderRadius: 1,
        typography: 'body2',
        fontFamily: EMOJI_FONT_FAMILY,
        bgcolor: 'background.neutral',
        ...(me && {
          color: 'grey.800',
          bgcolor: 'primary.lighter',
        }),
        ...(bubbleIsTransparent && {
          p: 0,
          bgcolor: 'transparent',
        }),
      }}
    >
      {hasImage ? (
        <Box
          component="img"
          alt="attachment"
          src={body}
          onClick={() => onOpenLightbox(body)}
          sx={{
            minHeight: 220,
            borderRadius: 1.5,
            cursor: 'pointer',
            '&:hover': { opacity: 0.9 },
          }}
        />
      ) : (
        <>
          {replyQuote && (
            <Box
              onClick={replyQuote.replyId ? handleReplyClick : undefined}
              sx={{
                px: 1,
                py: 0.75,
                borderLeft: '3px solid',
                borderLeftColor: me ? 'primary.main' : 'info.main',
                bgcolor: (theme) => (me ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.7)'),
                borderRadius: 0.75,
                mb: 0.5,
                cursor: replyQuote.replyId ? 'pointer' : 'default',
                '&:hover': replyQuote.replyId ? { filter: 'brightness(0.96)' } : undefined,
              }}
            >
              {replyQuote.author && (
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: me ? 'primary.dark' : 'info.dark' }}>
                  {replyQuote.author}
                </Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {replyQuote.body}
              </Typography>
            </Box>
          )}

          {hasRestText && (
            <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {linkify(bodyRest, handleMentionClick)}
            </Box>
          )}

          {videoLinks.length > 0 && (
            <Stack spacing={0.75} sx={{ mt: hasTextBody ? 0.5 : 0 }}>
              {videoLinks.map((v) => (
                <Box
                  key={v.embed}
                  onClick={() => setVideoDialog(v)}
                  sx={{
                    position: 'relative',
                    width: 280,
                    height: 158,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    bgcolor: 'grey.900',
                    cursor: 'pointer',
                    '&:hover .play-overlay': { transform: 'scale(1.08)' },
                  }}
                >
                  {v.provider === 'youtube' && (
                    <Box
                      component="img"
                      src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                      alt="thumbnail"
                      sx={{ width: 1, height: 1, objectFit: 'cover', opacity: 0.85 }}
                    />
                  )}
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box className="play-overlay" sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', transition: 'transform 0.15s ease-out' }}>
                      <Iconify icon="solar:play-bold" width={24} sx={{ color: 'white' }} />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: 'rgba(0,0,0,0.65)',
                      color: 'white',
                      fontSize: 10,
                      textTransform: 'capitalize',
                      fontWeight: 600,
                    }}
                  >
                    {v.provider}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}

          {hasInlineImages && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {imageAttachments.map((url, idx) => (
                <Box
                  key={idx}
                  component="img"
                  alt={`attachment-${idx}`}
                  src={url}
                  onClick={() => onOpenLightbox && onOpenLightbox(url)}
                  sx={{
                    width: imageAttachments.length > 1 ? 120 : 220,
                    height: imageAttachments.length > 1 ? 120 : 'auto',
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.9 },
                  }}
                />
              ))}
            </Stack>
          )}

          {hasInlineFiles && (
            <Stack spacing={0.5}>
              {fileAttachments.map((url, idx) => {
                const name = (url.split('/').pop() || 'file').split('?')[0];
                return (
                  <Stack
                    key={idx}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 0.75,
                      borderRadius: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Iconify icon="solar:document-bold-duotone" width={18} />
                    <Box sx={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </Box>
                    <Iconify icon="eva:download-outline" width={14} sx={{ ml: 'auto', opacity: 0.6 }} />
                  </Stack>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );

  const renderActions = (
    <Stack
      direction="row"
      className="message-actions"
      sx={{
        pt: 0.5,
        opacity: 0,
        top: '100%',
        left: 0,
        position: 'absolute',
        transition: (theme) =>
          theme.transitions.create(['opacity'], {
            duration: theme.transitions.duration.shorter,
          }),
        ...(me && {
          left: 'unset',
          right: 0,
        }),
      }}
    >
      {onReply && (
        <Tooltip title="Reply">
          <IconButton size="small" onClick={() => onReply(message)}>
            <Iconify icon="solar:reply-bold" width={16} />
          </IconButton>
        </Tooltip>
      )}
      {onReact && (
        <Tooltip title="React">
          <IconButton size="small" onClick={(e) => setReactionAnchor(e.currentTarget)}>
            <Iconify icon="eva:smiling-face-fill" width={16} />
          </IconButton>
        </Tooltip>
      )}
      {me && onDelete && (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(message)}>
            <Iconify icon="solar:trash-bin-trash-bold" width={16} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  const renderReactionPopover = (
    <Popover
      open={Boolean(reactionAnchor)}
      anchorEl={reactionAnchor}
      onClose={() => setReactionAnchor(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Stack direction="row" spacing={0.5} sx={{ p: 0.75 }}>
        {QUICK_REACTIONS.map((emoji) => (
          <IconButton
            key={emoji}
            size="small"
            onClick={() => {
              if (onReact) onReact(message, emoji);
              setReactionAnchor(null);
            }}
            sx={{ fontSize: 18, width: 32, height: 32, fontFamily: EMOJI_FONT_FAMILY }}
          >
            {emoji}
          </IconButton>
        ))}
      </Stack>
    </Popover>
  );

  return (
    <Stack
      id={`msg-${message.id}`}
      direction="row"
      justifyContent={me ? 'flex-end' : 'unset'}
      sx={{
        mb: 5,
        scrollMarginTop: 80,
        transition: 'background-color 0.4s ease',
      }}
    >
      {!me && <Avatar alt={firstName} src={avatarUrl} sx={{ width: 32, height: 32, mr: 2 }} />}

      <Stack alignItems={me ? 'flex-end' : 'flex-start'}>
        {renderInfo}

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            position: 'relative',
            '&:hover': {
              '& .message-actions': {
                opacity: 1,
              },
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {renderBody}
            {effectiveReactions.length > 0 && (
              <Stack
                direction="row"
                spacing={0.25}
                sx={{
                  position: 'absolute',
                  bottom: -10,
                  left: 8,
                  bgcolor: 'background.paper',
                  borderRadius: 999,
                  px: 0.5,
                  py: 0.125,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: (t) => t.shadows[1],
                  zIndex: 1,
                }}
              >
                {(() => {
                  const counts = effectiveReactions.reduce((acc, r) => {
                    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                    return acc;
                  }, {});
                  return Object.entries(counts).map(([emoji, count]) => (
                    <Stack
                      key={emoji}
                      direction="row"
                      alignItems="center"
                      spacing={0.25}
                      sx={{ fontSize: 12, lineHeight: 1, px: 0.5 }}
                    >
                      <span style={{ fontSize: 14, fontFamily: EMOJI_FONT_FAMILY }}>{emoji}</span>
                      {count > 1 && (
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
                          {count}
                        </Typography>
                      )}
                    </Stack>
                  ));
                })()}
              </Stack>
            )}
          </Box>
          {renderActions}
        </Stack>
        {renderReactionPopover}
      </Stack>

      <Dialog
        open={Boolean(videoDialog)}
        onClose={() => setVideoDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          {videoDialog && (
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
              <Box
                component="iframe"
                src={`${videoDialog.embed}?autoplay=1`}
                title={`${videoDialog.provider}-${videoDialog.id}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

ChatMessageItem.propTypes = {
  message: PropTypes.object,
  onOpenLightbox: PropTypes.func,
  onReply: PropTypes.func,
  onReact: PropTypes.func,
  onDelete: PropTypes.func,
  participants: PropTypes.array,
};
